import { CarIcon, Clock3Icon, HeartIcon, HomeIcon, PillIcon, TargetIcon } from 'lucide-react'
import type { Service } from '../types/content'

export const services: Service[] = [
  {
    id: 'personal-care',
    icon: HeartIcon,
    name: { en: 'Personal Care Assistance', es: 'Asistencia de Cuidado Personal' },
    shortName: { en: 'Personal Care', es: 'Cuidado Personal' },
    summary: {
      en: "Help with bathing, grooming, dressing, and hygiene — delivered with dignity and respect for each individual's comfort.",
      es: 'Ayuda con el baño, aseo personal, vestimenta e higiene — entregada con dignidad y respeto por la comodidad de cada individuo.',
    },
    description: {
      en: 'Hands-on help with the activities of daily living, delivered with patience and privacy. Caregivers assist with bathing, grooming, dressing, toileting, mobility, and transfers, always encouraging your loved one to do what they can on their own.',
      es: 'Ayuda directa con las actividades de la vida diaria, con paciencia y privacidad. Los cuidadores ayudan con el baño, el aseo, vestirse, ir al baño, la movilidad y los traslados, siempre animando a tu ser querido a hacer lo que pueda por sí mismo.',
    },
    benefits: [
      { en: 'Safe showers, bed baths, and grooming', es: 'Duchas, baños en cama y aseo seguros' },
      { en: 'Dignified help with dressing and toileting', es: 'Ayuda digna para vestirse e ir al baño' },
      { en: 'Mobility and transfer assistance to prevent falls', es: 'Asistencia de movilidad para prevenir caídas' },
      { en: 'Same-gender caregivers on request', es: 'Cuidadores del mismo género si se solicita' },
    ],
    idealFor: {
      en: 'Seniors with limited mobility, recovering from illness, or who need physical support with everyday routines.',
      es: 'Personas mayores con movilidad limitada, en recuperación, o que necesitan apoyo físico en su rutina diaria.',
    },
    priceRange: { en: '$32–$36 / hour', es: '$32–$36 / hora' },
  },
  {
    id: 'companionship-homemaking',
    icon: HomeIcon,
    name: { en: 'Companionship & Homemaking', es: 'Compañerismo y Tareas Domésticas' },
    shortName: { en: 'Companionship', es: 'Compañerismo' },
    summary: {
      en: 'Light housekeeping, meal prep, laundry, and genuine companionship to keep the home running and spirits high.',
      es: 'Limpieza ligera, preparación de comidas, lavandería y compañía genuina para mantener el hogar en funcionamiento.',
    },
    description: {
      en: 'A tidy home, a warm meal, and someone to talk to make every day better. Caregivers prepare nutritious meals, handle laundry and light housekeeping, and share conversation, hobbies, and walks — in English or Spanish.',
      es: 'Un hogar ordenado, una comida caliente y alguien con quien conversar mejoran cada día. Los cuidadores preparan comidas nutritivas, se encargan de la ropa y la limpieza ligera, y comparten conversación, pasatiempos y caminatas, en inglés o español.',
    },
    benefits: [
      { en: 'Nutritious meals that follow dietary needs', es: 'Comidas nutritivas según las necesidades alimenticias' },
      { en: 'Laundry, dishes, and a clutter-free home', es: 'Ropa, platos y un hogar despejado' },
      { en: 'Conversation and activities that lift the mood', es: 'Conversación y actividades que levantan el ánimo' },
      { en: 'Companions who speak your language', es: 'Acompañantes que hablan tu idioma' },
    ],
    idealFor: {
      en: 'Seniors who live alone, find chores tiring, or whose family works long hours or lives far away.',
      es: 'Personas mayores que viven solas, se cansan con las tareas o cuya familia trabaja muchas horas o vive lejos.',
    },
    priceRange: { en: '$30–$34 / hour', es: '$30–$34 / hora' },
  },
  {
    id: 'medication-reminders',
    icon: PillIcon,
    name: { en: 'Medication Reminders', es: 'Recordatorios de Medicamentos' },
    shortName: { en: 'Medication Reminders', es: 'Recordatorios de Medicamentos' },
    summary: {
      en: 'Timely, careful oversight to ensure medications are taken correctly and on schedule — every single day.',
      es: 'Supervisión cuidadosa y oportuna para asegurar que los medicamentos se tomen correctamente y según el horario.',
    },
    description: {
      en: 'Missed or doubled doses are a leading cause of hospital visits for older adults. Caregivers provide timely reminders, keep a clear medication log, coordinate pharmacy refills, and let the family know right away if anything seems off.',
      es: 'Las dosis olvidadas o duplicadas son una causa principal de visitas al hospital en personas mayores. Los cuidadores dan recordatorios puntuales, llevan un registro claro, coordinan resurtidos en la farmacia y avisan de inmediato a la familia si algo no parece estar bien.',
    },
    benefits: [
      { en: 'On-time reminders for every dose', es: 'Recordatorios puntuales para cada dosis' },
      { en: 'Written medication log for the family', es: 'Registro escrito de medicamentos para la familia' },
      { en: 'Pharmacy pickups and refill tracking', es: 'Recogida en farmacia y control de resurtidos' },
      { en: 'Early notice of side effects or changes', es: 'Aviso temprano de efectos secundarios o cambios' },
    ],
    idealFor: {
      en: 'Seniors managing several prescriptions, memory changes, or a new treatment plan after a hospital stay.',
      es: 'Personas mayores con varias recetas, cambios de memoria o un nuevo tratamiento después de una hospitalización.',
    },
    priceRange: { en: 'Included with any care plan', es: 'Incluido en cualquier plan de cuidado' },
  },
  {
    id: 'transportation-errands',
    icon: CarIcon,
    name: { en: 'Transportation & Errands', es: 'Transporte y Encargos' },
    shortName: { en: 'Transportation', es: 'Transporte' },
    summary: {
      en: 'Safe, reliable transportation to appointments, grocery shopping, social activities, and any destination that matters.',
      es: 'Transporte seguro y confiable a citas, compras de comestibles, actividades sociales y cualquier destino que importe.',
    },
    description: {
      en: 'Staying connected to the world keeps seniors healthy. Caregivers drive and accompany your loved one to doctor visits, the pharmacy, the grocery store, church, and visits with friends — and wait with them so no one goes alone.',
      es: 'Mantenerse conectado con el mundo mantiene sanas a las personas mayores. Los cuidadores llevan y acompañan a tu ser querido a citas médicas, la farmacia, el supermercado, la iglesia y visitas con amigos, y se quedan con ellos para que nadie vaya solo.',
    },
    benefits: [
      { en: 'Door-to-door rides with a familiar caregiver', es: 'Traslados puerta a puerta con un cuidador conocido' },
      { en: 'Company and notes at medical appointments', es: 'Compañía y notas en las citas médicas' },
      { en: 'Grocery shopping and pharmacy pickups', es: 'Compras del mercado y recogida en farmacia' },
      { en: 'Social outings that prevent isolation', es: 'Salidas sociales que previenen el aislamiento' },
    ],
    idealFor: {
      en: 'Seniors who no longer drive, or families who can’t always leave work for appointments and errands.',
      es: 'Personas mayores que ya no manejan, o familias que no siempre pueden salir del trabajo para citas y mandados.',
    },
    priceRange: { en: '$30–$34 / hour', es: '$30–$34 / hora' },
  },
  {
    id: 'specialized-care',
    icon: TargetIcon,
    name: { en: 'Specialized Care', es: 'Cuidado Especializado' },
    shortName: { en: 'Specialized Care', es: 'Cuidado Especializado' },
    summary: {
      en: "Focused, patient support for Alzheimer's, dementia, Parkinson's, and conditions requiring dedicated expert care.",
      es: 'Apoyo enfocado y paciente para Alzheimer, demencia, Parkinson y condiciones que requieren cuidado experto dedicado.',
    },
    description: {
      en: "Memory loss and progressive conditions change daily life for the whole family. Our trained caregivers use calm redirection, structured routines, and memory-friendly activities, while keeping your loved one safe from wandering, falls, and missed meals.",
      es: 'La pérdida de memoria y las condiciones progresivas cambian la vida diaria de toda la familia. Nuestros cuidadores capacitados usan redirección tranquila, rutinas estructuradas y actividades para la memoria, manteniendo a tu ser querido a salvo de la desorientación, las caídas y las comidas olvidadas.',
    },
    benefits: [
      { en: 'Caregivers trained in dementia communication', es: 'Cuidadores capacitados en comunicación con demencia' },
      { en: 'Calm, consistent routines that reduce agitation', es: 'Rutinas tranquilas y constantes que reducen la agitación' },
      { en: "Mobility support for Parkinson's and stroke recovery", es: 'Apoyo de movilidad para Parkinson y recuperación de derrames' },
      { en: 'Overnight and 24-hour supervision available', es: 'Supervisión nocturna y de 24 horas disponible' },
    ],
    idealFor: {
      en: "Families caring for someone with Alzheimer's, dementia, Parkinson's, or another condition that needs dedicated expertise.",
      es: 'Familias que cuidan a alguien con Alzheimer, demencia, Parkinson u otra condición que requiere experiencia dedicada.',
    },
    priceRange: { en: '$34–$38 / hour', es: '$34–$38 / hora' },
  },
  {
    id: 'respite-care',
    icon: Clock3Icon,
    name: { en: 'Respite Care', es: 'Cuidado de Relevo' },
    shortName: { en: 'Respite Care', es: 'Cuidado de Relevo' },
    summary: {
      en: 'Relief for family caregivers — we step in for a few hours or extended stays so you can recharge without worry.',
      es: 'Alivio para cuidadores familiares — nos encargamos por unas pocas horas o estadías prolongadas para que puedas descansar sin preocupaciones.',
    },
    description: {
      en: 'Caring for a parent or spouse is an act of love, and it is exhausting. Our caregivers follow your routines exactly, so you can work, travel, rest, or simply take an afternoon for yourself, knowing your loved one is in good hands.',
      es: 'Cuidar a un padre, madre o pareja es un acto de amor, y también es agotador. Nuestros cuidadores siguen tus rutinas al pie de la letra, para que puedas trabajar, viajar, descansar o simplemente tomarte una tarde, sabiendo que tu ser querido está en buenas manos.',
    },
    benefits: [
      { en: 'A few hours, overnight, or multi-day coverage', es: 'Unas horas, una noche o varios días' },
      { en: 'Your routines and preferences followed exactly', es: 'Tus rutinas y preferencias respetadas al detalle' },
      { en: 'Prevents family caregiver burnout', es: 'Previene el agotamiento del cuidador familiar' },
      { en: 'Book once or on a regular schedule', es: 'Reserva una vez o de forma regular' },
    ],
    idealFor: {
      en: 'Family members who provide most of the care and need time to rest, work, travel, or handle an emergency.',
      es: 'Familiares que brindan la mayor parte del cuidado y necesitan tiempo para descansar, trabajar, viajar o atender una emergencia.',
    },
    priceRange: { en: 'From $30 / hour', es: 'Desde $30 / hora' },
  },
]
